// src/pages/SupportReports.jsx
// Dashboard de Atención a Clientes. Rediseño 2026-05-13. Lógica 1:1.
import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';


const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    refresh: <Icon size={14}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Icon>,
    chat:    <Icon size={14}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>,
    target:  <Icon size={14}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></Icon>,
    check:   <Icon size={14}><path d="M20 6 9 17l-5-5" /></Icon>,
    bot:     <Icon size={14}><rect x="4" y="6" width="16" height="14" rx="2" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" /><path d="M12 2v4M8 20v2M16 20v2" /></Icon>,
};


function fmtMs(ms) {
    if (ms == null) return '—';
    const sec = Math.round(ms / 1000);
    if (sec < 60)    return `${sec}s`;
    const m = Math.floor(sec / 60);
    if (m < 60)      return `${m} min ${sec % 60}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m - h * 60}m`;
}
function pct(n) { return n == null ? '—' : `${(n * 100).toFixed(1)}%`; }
function isoDateInput(d) { return d.toISOString().slice(0, 10); }
const defaultFrom = () => { const d = new Date(); d.setDate(d.getDate() - 6); d.setHours(0,0,0,0); return d; };
const defaultTo   = () => { const d = new Date(); d.setHours(23,59,59,999); return d; };

const STATUS_LABEL = { open: 'Nueva', bot: 'Bot', escalated: 'Escalada', active: 'En atención', closed: 'Cerrada' };
const STATUS_COLOR = { open: '#1e40af', bot: '#5b21b6', escalated: '#92400e', active: '#065f46', closed: '#6b7280' };


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.sp-root {
    --sp-accent: var(--brand-700, #0a4aa8);
    --sp-accent-hover: var(--brand-800, #07357f);
    --sp-accent-ring: rgba(10, 74, 168, .12);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1280px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.sp-root * { box-sizing: border-box; }
.sp-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.sp-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0 0 20px; line-height: 1.5; }

.sp-filters {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
    padding: 14px 16px; margin-bottom: 16px;
    display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap;
}
.sp-field { display: flex; flex-direction: column; }
.sp-field label {
    font-size: 11px; font-weight: 600; letter-spacing: 0.3px;
    color: var(--c-text-2); margin-bottom: 4px;
}
.sp-input {
    padding: 7px 11px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none; max-width: 160px;
    transition: border-color .15s, box-shadow .15s;
}
.sp-input:focus { border-color: var(--sp-accent); box-shadow: 0 0 0 3px var(--sp-accent-ring); }

.sp-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 8px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.sp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sp-btn-primary { background: var(--sp-accent); color: #fff; border-color: var(--sp-accent); }
.sp-btn-primary:hover:not(:disabled) { background: var(--sp-accent-hover); border-color: var(--sp-accent-hover); }

.sp-generated { margin-left: auto; font-size: 11px; color: var(--c-text-4); font-variant-numeric: tabular-nums; }

.sp-kpi-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 12px; margin-bottom: 16px;
}
.sp-kpi {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-left: 4px solid var(--kpi-color, var(--sp-accent));
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
    transition: transform .15s, box-shadow .15s;
}
.sp-kpi:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(15,23,42,.06); }
.sp-kpi-head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 8px;
}
.sp-kpi-label {
    font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase; color: var(--c-text-4); margin: 0;
}
.sp-kpi-icon { color: var(--kpi-color, var(--sp-accent)); display: inline-flex; }
.sp-kpi-value {
    font-size: 28px; font-weight: 800; color: var(--c-text-1);
    margin: 0; line-height: 1; letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
}
.sp-kpi-sub { font-size: 11px; color: var(--c-text-3); margin: 6px 0 0; }

.sp-section {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 12px; padding: 16px 18px; margin-bottom: 12px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.sp-section-title {
    font-size: 13px; font-weight: 600; color: var(--c-text-2);
    margin: 0 0 12px; letter-spacing: 0.1px;
}

.sp-cols-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 720px) { .sp-cols-2 { grid-template-columns: 1fr; } }

.sp-sla-card { background: var(--c-msg-bg); border-radius: 10px; padding: 14px; }
.sp-sla-title { font-size: 12.5px; font-weight: 700; color: var(--c-text-2); margin: 0 0 10px; }
.sp-sla-row { display: flex; gap: 18px; flex-wrap: wrap; }
.sp-sla-item { min-width: 80px; }
.sp-sla-item-lbl { font-size: 10px; color: var(--c-text-4); text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
.sp-sla-item-val { font-size: 18px; font-weight: 700; margin-top: 2px; font-variant-numeric: tabular-nums; }
.sp-sla-item-val.small { font-size: 13px; font-weight: 600; }
.sp-sla-meta { font-size: 11px; color: var(--c-text-4); margin-top: 8px; }

.sp-bar { display: flex; flex-direction: column; gap: 7px; }
.sp-bar-row { display: flex; align-items: center; gap: 10px; font-size: 12.5px; }
.sp-bar-label { min-width: 140px; color: var(--c-text-2); }
.sp-bar-track { flex: 1; background: var(--c-msg-bg); border-radius: 5px; height: 18px; position: relative; }
.sp-bar-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 5px; transition: width .3s ease; }
.sp-bar-count { min-width: 60px; text-align: right; font-weight: 700; color: var(--c-text-1); font-variant-numeric: tabular-nums; }

.sp-daily { display: flex; align-items: flex-end; gap: 4px; height: 140px; padding: 8px 4px 0; }
.sp-daily-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; min-width: 0; }
.sp-daily-count { font-size: 10px; color: var(--c-text-4); font-variant-numeric: tabular-nums; }
.sp-daily-bar {
    width: 100%; background: var(--sp-accent); border-radius: 4px 4px 0 0;
    min-height: 2px; transition: height .3s;
}
.sp-daily-date {
    font-size: 9.5px; color: var(--c-text-4); transform: rotate(-30deg);
    transform-origin: top right; white-space: nowrap; padding-right: 6px;
}
.sp-empty { font-size: 12px; color: var(--c-text-4); }
`;


function KpiCard({ title, value, sub, color, icon }) {
    return (
        <div className="sp-kpi" style={{ '--kpi-color': color }}>
            <div className="sp-kpi-head">
                <p className="sp-kpi-label">{title}</p>
                {icon && <span className="sp-kpi-icon">{icon}</span>}
            </div>
            <p className="sp-kpi-value">{value}</p>
            <p className="sp-kpi-sub">{sub}</p>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="sp-section">
            <h3 className="sp-section-title">{title}</h3>
            {children}
        </div>
    );
}

function BarList({ items }) {
    if (!items?.length) return <div className="sp-empty">Sin datos.</div>;
    const max = Math.max(1, ...items.map(i => i.count));
    return (
        <div className="sp-bar">
            {items.map((it, i) => (
                <div key={i} className="sp-bar-row">
                    <div className="sp-bar-label">{it.label}</div>
                    <div className="sp-bar-track">
                        <div className="sp-bar-fill"
                            style={{ width: `${(it.count / max) * 100}%`, background: it.color || 'var(--sp-accent)' }} />
                    </div>
                    <div className="sp-bar-count">{it.count.toLocaleString()}</div>
                </div>
            ))}
        </div>
    );
}

function SlaDetailCard({ label, stats, target }) {
    if (!stats || !stats.count) {
        return (
            <div className="sp-sla-card">
                <p className="sp-sla-title">{label}</p>
                <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: 0 }}>Sin datos en el rango.</p>
            </div>
        );
    }
    const rate = stats.rate ?? 0;
    const color = rate >= 0.9 ? '#15803d' : rate >= 0.7 ? '#b45309' : '#b91c1c';
    return (
        <div className="sp-sla-card">
            <p className="sp-sla-title">{label}</p>
            <div className="sp-sla-row">
                <div className="sp-sla-item">
                    <div className="sp-sla-item-lbl">Cumplimiento</div>
                    <div className="sp-sla-item-val" style={{ color }}>{pct(rate)}</div>
                </div>
                <div className="sp-sla-item">
                    <div className="sp-sla-item-lbl">Promedio</div>
                    <div className="sp-sla-item-val small">{fmtMs(stats.avgMs)}</div>
                </div>
                <div className="sp-sla-item">
                    <div className="sp-sla-item-lbl">p50 / p95</div>
                    <div className="sp-sla-item-val small">{fmtMs(stats.p50Ms)} / {fmtMs(stats.p95Ms)}</div>
                </div>
            </div>
            <div className="sp-sla-meta">
                Meta: {target} min · {stats.metSla} de {stats.count} convs cumplieron.
            </div>
        </div>
    );
}


export default function SupportReports() {
    const [from, setFrom]       = useState(isoDateInput(defaultFrom()));
    const [to,   setTo]         = useState(isoDateInput(defaultTo()));
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = {
                from: new Date(`${from}T00:00:00`).toISOString(),
                to:   new Date(`${to}T23:59:59`).toISOString(),
            };
            const { data: res } = await api.get('/support/reports', { params });
            setData(res);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cargar');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const maxDaily = useMemo(() => {
        if (!data?.daily?.length) return 1;
        return Math.max(1, ...data.daily.map(d => d.count));
    }, [data]);

    return (
        <>
            <style>{CSS}</style>
            <div className="sp-root">
                <h1 className="sp-title">Reportes — Atención a Clientes</h1>
                <p className="sp-subtitle">
                    Métricas agregadas del módulo. Default: últimos 7 días. Cambia el rango con los selectores.
                </p>

                <div className="sp-filters">
                    <div className="sp-field">
                        <label>Desde</label>
                        <input type="date" className="sp-input" value={from} onChange={e => setFrom(e.target.value)} />
                    </div>
                    <div className="sp-field">
                        <label>Hasta</label>
                        <input type="date" className="sp-input" value={to} onChange={e => setTo(e.target.value)} />
                    </div>
                    <button type="button" className="sp-btn sp-btn-primary" onClick={load} disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" /> : <>{I.refresh} Actualizar</>}
                    </button>
                    {data?.generatedAt && (
                        <span className="sp-generated">
                            Generado: {new Date(data.generatedAt).toLocaleString()}
                        </span>
                    )}
                </div>

                {loading || !data ? (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" variant="primary" /></div>
                ) : (
                    <>
                        <div className="sp-kpi-grid">
                            <KpiCard
                                title="Conversaciones"
                                value={data.totals.conversations.toLocaleString()}
                                sub={`Cerradas: ${(data.totals.byStatus.find(s => s.status === 'closed')?.count || 0).toLocaleString()}`}
                                color="#2563eb"
                                icon={I.chat}
                            />
                            <KpiCard
                                title="SLA primera respuesta"
                                value={pct(data.sla.firstResponse?.rate)}
                                sub={`promedio ${fmtMs(data.sla.firstResponse?.avgMs)} (meta ${data.sla.firstResponseMinutes} min)`}
                                color={(data.sla.firstResponse?.rate ?? 1) >= 0.9 ? '#15803d' : (data.sla.firstResponse?.rate ?? 1) >= 0.7 ? '#b45309' : '#b91c1c'}
                                icon={I.target}
                            />
                            <KpiCard
                                title="SLA resolución"
                                value={pct(data.sla.resolution?.rate)}
                                sub={`promedio ${fmtMs(data.sla.resolution?.avgMs)} (meta ${data.sla.resolutionMinutes} min)`}
                                color={(data.sla.resolution?.rate ?? 1) >= 0.9 ? '#15803d' : (data.sla.resolution?.rate ?? 1) >= 0.7 ? '#b45309' : '#b91c1c'}
                                icon={I.check}
                            />
                            <KpiCard
                                title="Resueltas por bot"
                                value={data.bot.resolvedByBot.toLocaleString()}
                                sub={`${pct(data.bot.resolutionRate)} del total — sin tocar humano`}
                                color="#7c3aed"
                                icon={I.bot}
                            />
                        </div>

                        <Section title="Detalle de SLA">
                            <div className="sp-cols-2">
                                <SlaDetailCard
                                    label="Primera respuesta humana"
                                    stats={data.sla.firstResponse}
                                    target={data.sla.firstResponseMinutes}
                                />
                                <SlaDetailCard
                                    label="Resolución total"
                                    stats={data.sla.resolution}
                                    target={data.sla.resolutionMinutes}
                                />
                            </div>
                        </Section>

                        <Section title="Por status (snapshot actual)">
                            <BarList items={data.totals.byStatus.map(s => ({
                                label: STATUS_LABEL[s.status] || s.status,
                                count: s.count,
                                color: STATUS_COLOR[s.status],
                            }))} />
                        </Section>

                        {data.daily?.length > 0 && (
                            <Section title="Conversaciones por día">
                                <div className="sp-daily">
                                    {data.daily.map(d => (
                                        <div key={d.date} className="sp-daily-col">
                                            <span className="sp-daily-count">{d.count}</span>
                                            <div className="sp-daily-bar"
                                                style={{ height: `${(d.count / maxDaily) * 100}px` }}
                                                title={`${d.date}: ${d.count}`} />
                                            <span className="sp-daily-date">{d.date.slice(5)}</span>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {data.byAgent?.length > 0 && (
                            <Section title="Top agentes">
                                <BarList items={data.byAgent.map(a => ({ label: a.name, count: a.count, color: '#0f766e' }))} />
                            </Section>
                        )}

                        <div className="sp-cols-2">
                            {data.byMarca?.length > 0 && (
                                <Section title="Por marca">
                                    <BarList items={data.byMarca.map(m => ({ label: m.marca, count: m.count, color: '#0891b2' }))} />
                                </Section>
                            )}
                            {data.byPais?.length > 0 && (
                                <Section title="Por país">
                                    <BarList items={data.byPais.map(p => ({ label: p.pais, count: p.count, color: '#db2777' }))} />
                                </Section>
                            )}
                        </div>

                        {data.closures?.byActor?.length > 0 && (
                            <Section title="Cierres por actor">
                                <BarList items={data.closures.byActor.map(c => ({
                                    label: c.by === 'client' ? 'Cliente' : c.by === 'agent' ? 'Agente' : c.by === 'system' ? 'Sistema' : c.by,
                                    count: c.count,
                                    color: c.by === 'client' ? '#dc2626' : '#0f766e',
                                }))} />
                            </Section>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
